import { activeScrollTarget } from '@/components/ui/activeScrollTarget';
import { PAGE_GUTTER, usePageFrame } from '@/components/ui/pageFrame';
import { space } from '@/constants/tokens';
import { useIsFocused } from 'expo-router';
import { type Ref, useEffect, useRef } from 'react';
import {
  Platform,
  type ScrollView as RNScrollView,
  type ScrollViewProps,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { ScrollView } from 'tamagui';

/**
 * The standard scrolling frame for a top-level screen. EVERY full-page screen
 * (Home, Profile, the event/confirm/booking screens) shares the same frame:
 *
 *   - fills the screen (`flex: 1`) so it sits above a flex-sibling bottom bar,
 *   - hides the scroll indicator,
 *   - insets its content by the page gutter (`$lg`) on both sides,
 *   - starts its content BELOW the transparent native header (all our headers
 *     are `headerTransparent: true`, so content scrolls up under them — the top
 *     padding is the header's measured height, which already includes the
 *     safe-area notch inset, so the first row clears the header controls), and
 *   - leaves a bottom gap (`$xxl`) so the last row clears the screen edge / bar.
 *
 * It exists so a new screen falls into that frame automatically: write
 * `<PageScrollView>…</PageScrollView>` and the gutter, the under-header start
 * offset, and the bottom gap are all correct with nothing to remember. None of
 * the FRAME NUMBERS are overridable ON PURPOSE — a per-screen knob is exactly
 * how the frame drifted before (home/profile at +60, confirm/booking at +50
 * for no reason). `keyboardAware` is the one opt-in exception: it swaps the
 * underlying scroll engine for a screen whose focused field needs real
 * keyboard-avoidance, but computes the exact same gutter/padding numbers —
 * see the prop's doc.
 * The props are RN's ScrollViewProps, not Tamagui's, so the same `rest` spreads
 * into either engine. No screen passes a Tamagui style prop here anyway.
 * One frame, one place: the numbers live in [[pageFrame]], which [[PageFlatList]]
 * — the same frame around a virtualized page — reads from too.
 *
 * `ref` forwards to the underlying RN ScrollView so a screen can still drive it
 * imperatively (the event page scrolls to its picker via `scrollTo`). Extra
 * ScrollView props spread through for the rare passthrough (e.g. `onScroll`),
 * but the frame props above are fixed.
 *
 * While focused, on web, it also publishes its DOM node to
 * [[activeScrollTarget]] — AppColumn's margin has no scrollable ancestor of
 * its own, so it forwards wheel events there instead.
 *
 * ── Pull-to-refresh ──
 * Pass `onRefresh` + `isRefreshing` and the frame builds the RefreshControl. It
 * lives here rather than at each screen because both of the awkward bits are
 * things the frame already knows and a screen would have to re-derive:
 *
 *   - WHERE the spinner goes. Under a transparent header the content starts well
 *     down the screen, and a control left at 0 turns behind the glass bar; with
 *     `headerTransparent={false}` the same sum collapses to just the top gap.
 *     It's the SAME expression as `pt`, so the spinner lands on the content's top
 *     edge and the two can't drift apart.
 *   - THAT web doesn't get one. RN-web drops every refresh prop on the floor and
 *     then wraps the ScrollView in a View carrying the SAME style, so the page
 *     pads itself twice — a spinner that can't spin, in exchange for a visibly
 *     shifted layout. Browsers have a reload button.
 */

function PageScrollView({
  children,
  headerTransparent = true,
  onRefresh,
  isRefreshing = false,
  keyboardAware = false,
  ref,
  ...rest
}: ScrollViewProps & { ref?: Ref<RNScrollView> } & {
  headerTransparent?: boolean;
  /** Omit for a page that doesn't pull to refresh — then no control is built. */
  onRefresh?: () => void;
  isRefreshing?: boolean;
  /**
   * Opt in when a field below the fold gets hidden by the keyboard on focus.
   * RN has no built-in scroll-to-focused-input: iOS has one native prop
   * (`automaticallyAdjustKeyboardInsets`, off by default) and Android has
   * nothing at all now that edge-to-edge stops `adjustResize` from resizing the
   * window. Hence the library. Off by default so the other ~50 screens on this
   * frame keep the engine they were built against.
   */
  keyboardAware?: boolean;
}) {
  const { paddingTop, paddingBottom, refreshControl } = usePageFrame({
    headerTransparent,
    onRefresh,
    isRefreshing,
  });

  const internalRef = useRef<RNScrollView>(null);
  const isFocused = useIsFocused();
  useEffect(() => {
    if (Platform.OS !== 'web' || !isFocused) return;
    const node = internalRef.current?.getScrollableNode();
    if (!node) return;
    activeScrollTarget.current = node;
    return () => {
      if (activeScrollTarget.current === node)
        activeScrollTarget.current = null;
    };
  }, [isFocused]);

  const setRef = (node: RNScrollView | null) => {
    internalRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  if (keyboardAware) {
    return (
      <KeyboardAwareScrollView
        {...rest}
        ref={setRef}
        style={{ flex: 1, paddingHorizontal: PAGE_GUTTER }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        // The gap left between the focused field and the keyboard's top edge.
        bottomOffset={space.lg}
        refreshControl={refreshControl}
        contentContainerStyle={{ paddingTop, paddingBottom }}
      >
        {children}
      </KeyboardAwareScrollView>
    );
  }

  return (
    <ScrollView
      {...rest}
      ref={setRef}
      flex={1}
      showsVerticalScrollIndicator={false}
      // A touchable below a focused text input must fire on the first tap, not
      // spend it dismissing the keyboard (RN's ScrollView default) with a second
      // tap needed to actually land the press.
      keyboardShouldPersistTaps="handled"
      px={PAGE_GUTTER}
      refreshControl={refreshControl}
      contentContainerStyle={{
        pt: paddingTop,
        pb: paddingBottom,
      }}
    >
      {children}
    </ScrollView>
  );
}

export default PageScrollView;
